/**
 * A very small History-API router, aware of the locale prefix.
 *
 * The design prototype had no URLs at all — it switched on a `page` field in
 * component state, so nothing was linkable, shareable or reloadable. Production
 * needs real paths, but not a routing library: the site has a fixed, shallow
 * set of routes and no nested layouts, so this is about forty lines instead of
 * a dependency.
 *
 * ── The locale is a prefix, and the rest of the app never sees it ───────────
 * Every page lives under `/<locale>/…` (shared/locales.ts). `path` here is the
 * pathname with that prefix removed, so every route comparison stays a literal
 * `path === '/incidents'` rather than a match that has to know about locales.
 *
 * `navigate` reads the locale from `window.location` at call time rather than
 * closing over it. Closing over it would leave every callback made before a
 * locale switch pointing at the old one until it was rebuilt, and rebuilding
 * it re-fires every effect that depends on it.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, isLocale, localeOf, localePath, splitLocale } from '@shared/locales';
import { currentLocale, writeLocaleCookie } from './lib/locale';
import { syncHeadLinks } from './i18n/meta';

interface RouterValue {
  /** The locale being read: `en-gb`. */
  locale: string;
  /** The pathname without its locale prefix: `/incidents`. */
  path: string;
  query: URLSearchParams;
  navigate: (to: string, opts?: { replace?: boolean; keepScroll?: boolean }) => void;
  /** Move to the same page under another locale, and remember the choice. */
  setLocale: (tag: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [search, setSearch] = useState(() => window.location.search);

  useEffect(() => {
    // Back/forward buttons.
    const onPop = () => {
      setPathname(window.location.pathname);
      setSearch(window.location.search);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, opts?: { replace?: boolean; keepScroll?: boolean }) => {
    const url = new URL(localePath(currentLocale(), to), window.location.origin);
    // An external link should leave the SPA rather than being pushed onto our
    // own history stack.
    if (url.origin !== window.location.origin) {
      window.location.href = to;
      return;
    }

    if (opts?.replace) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);

    setPathname(url.pathname);
    setSearch(url.search);
    if (!opts?.keepScroll) window.scrollTo({ top: 0 });
  }, []);

  const setLocale = useCallback(
    (next: string) => {
      if (!isLocale(next)) return;
      writeLocaleCookie(next);
      // The same page and the same query under the new prefix — never the home
      // page. Somebody switching language while reading an incident wants that
      // incident in the other language.
      const { rest } = splitLocale(window.location.pathname);
      navigate(`/${next}${rest}${window.location.search}`, { keepScroll: true });
    },
    [navigate]
  );

  const value = useMemo<RouterValue>(() => {
    const { locale, rest } = splitLocale(pathname);
    return {
      locale: locale ?? DEFAULT_LOCALE,
      path: rest,
      query: new URLSearchParams(search),
      navigate,
      setLocale
    };
  }, [pathname, search, navigate, setLocale]);

  // The Worker writes `lang`, the canonical URL and the hreflang links into the
  // shell on the way out; this keeps them right after a client-side change of
  // page or locale, and covers the Vite dev server, which bypasses the Worker.
  useEffect(() => {
    document.documentElement.lang = localeOf(value.locale).intl;
    syncHeadLinks(value.locale, value.path);
  }, [value.locale, value.path]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter used outside RouterProvider');
  return ctx;
}

/** Anchor that stays inside the SPA, while remaining a real link for
 *  middle-click, ctrl-click and assistive tech. */
export function Link({
  to,
  children,
  style,
  className,
  onClick
}: {
  to: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
}) {
  const { locale, navigate } = useRouter();

  return (
    <a
      // The real href carries the prefix so a middle-click, a copied link and
      // a crawler all land on the locale being read. `navigate` re-adds it too;
      // `localePath` is idempotent, so callers never have to think about it.
      href={localePath(locale, to)}
      className={className}
      style={{ textDecoration: 'none', color: 'inherit', ...style }}
      onClick={(e) => {
        // Let the browser handle modified clicks so "open in new tab" works.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/** Match `/apps/:key` style patterns. Returns params, or null. */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(a[i]!);
    else if (seg !== a[i]) return null;
  }
  return params;
}
